import { ChangeEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui-kit/avatar';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useReportUnreportedError } from '@/state/query-client/hooks';
import { cn } from '@/lib/utils';
import { useProfilePhotoUrl, useUploadProfilePhoto } from '../../../hooks/use-profile-photo';

const DummyProfile = '/assets/images/dummy_profile.png';

/** Legacy limit for the profile photo. */
const MAX_PHOTO_SIZE_MB = 10;

export interface ProfilePhotoUploadProps {
  /** Blocks storage file id of the current photo (`Employee.ProfileImageId`). */
  fileId: string;
  onChange: (fileId: string) => void;
}

/** Round profile photo with an edit overlay; picking an image uploads it to Blocks storage right away. */
export const ProfilePhotoUpload = ({ fileId, onChange }: ProfilePhotoUploadProps) => {
  const { t } = useTranslation();
  const reportError = useReportUnreportedError();
  const inputId = useId();
  const [isTooLarge, setIsTooLarge] = useState(false);
  // URL of a photo uploaded in this session; saves a lookup round-trip for the file id it produced.
  const [uploaded, setUploaded] = useState<{ fileId: string; url: string } | null>(null);

  const { mutateAsync: uploadPhoto, isPending: isUploading } = useUploadProfilePhoto();
  const { data: savedUrl, isLoading: isSavedUrlLoading } = useProfilePhotoUrl(
    uploaded?.fileId === fileId ? undefined : fileId
  );

  const photoUrl = uploaded?.fileId === fileId ? uploaded.url : savedUrl;

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      setIsTooLarge(false);
      return;
    }
    if (file.size / 1000 / 1000 > MAX_PHOTO_SIZE_MB) {
      setIsTooLarge(true);
      return;
    }
    setIsTooLarge(false);

    try {
      const result = await uploadPhoto(file);
      if (!result.fileId) throw new Error('SOMETHING_WENT_WRONG');
      setUploaded({ fileId: result.fileId, url: result.fileUrl });
      onChange(result.fileId);
    } catch (error) {
      // A failed upload is already reported by the mutation; only a missing file id is reported here.
      reportError(error);
    }
  };

  return (
    <div className="flex shrink-0 flex-col items-center gap-2">
      <label
        htmlFor={inputId}
        title={t('ADD_AN_ATTACHMENT')}
        className={cn(
          'group relative cursor-pointer',
          isUploading && 'pointer-events-none animate-pulse'
        )}
      >
        <Avatar className="size-28 ring-4 ring-accent">
          <AvatarImage src={photoUrl || undefined} alt="" className="object-cover" />
          {/* Not the dummy avatar while the saved photo's link is on its way. */}
          <AvatarFallback>
            {isSavedUrlLoading ? (
              <Skeleton className="size-full rounded-full" />
            ) : (
              <img src={DummyProfile} alt="" className="size-full object-cover" />
            )}
          </AvatarFallback>
        </Avatar>
        <span className="absolute bottom-0 right-0 flex size-7 items-center justify-center rounded-full border bg-card shadow-sm">
          <Pencil className="size-4" />
        </span>
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="sr-only"
        aria-label={t('ADD_AN_ATTACHMENT')}
        onChange={handleChange}
        disabled={isUploading}
      />
      {isTooLarge && (
        <p className="text-[0.8rem] font-medium text-destructive">
          {t('MAX_SIZE')} {t('IS_10MB')}
        </p>
      )}
    </div>
  );
};
